import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import YouTube from 'react-youtube';
import {
  FiShield, FiTruck, FiTool, FiAward, FiDisc, FiZap, FiSun, FiWind, FiFilter, FiCircle, FiPackage, FiGrid,
  FiArrowRight, FiCamera, FiNavigation
} from 'react-icons/fi';
import {
  GiCarnivalMask, GiBullHorns, GiFullMotorcycleHelmet, GiCarSeat, GiLightBulb, GiLightProjector,
  GiWaterSplash, GiSteelwingEmblem, GiToolbox, GiCog, GiFuelTank, GiLayeredArmor, GiNoseFront,
  GiSteeringWheel, GiMirrorMirror, GiPackedPlanks, GiSpray, GiPerfumeBottle, GiStraightPipe,
  GiVisoredHelm, GiSpeedometer
} from 'react-icons/gi';
import { MdSmartphone } from 'react-icons/md';
import { FaCarSide } from 'react-icons/fa6';
import api, { getImageUrl, handleImageError } from '../utils/api';
import ProductCard from '../components/ProductCard';
import LoadingSpinner from '../components/LoadingSpinner';
import GarageScene from '../components/GarageScene';

const CATEGORY_ICONS = {
  brakes: <FiDisc size={28} />,
  engine: <FiZap size={28} />,
  lighting: <FiSun size={28} />,
  suspension: <FiWind size={28} />,
  electrical: <FiZap size={28} />,
  accessories: <FiPackage size={28} />,
  'tyres-wheels': <FiCircle size={28} />,
  filters: <FiFilter size={28} />,
  'body-parts': <FiShield size={28} />,
  tools: <FiTool size={28} />,
  mask: <GiCarnivalMask size={28} />,
  horn: <GiBullHorns size={28} />,
  'bike-accessories': <GiFullMotorcycleHelmet size={28} />,
  'car-accessories': <GiCarSeat size={28} />,
  'led-items': <GiLightBulb size={28} />,
  camera: <FiCamera size={28} />,
  'headlight-doom': <GiLightProjector size={28} />,
  mudguards: <GiWaterSplash size={28} />,
  'air-filters': <FiFilter size={28} />,
  'tail-panel': <GiSteelwingEmblem size={28} />,
  'additional-accessories': <GiToolbox size={28} />,
  tpfc: <GiCog size={28} />,
  'tank-cover': <GiFuelTank size={28} />,
  'side-panel': <GiLayeredArmor size={28} />,
  'front-nose': <GiNoseFront size={28} />,
  'dash-board': <GiSteeringWheel size={28} />,
  indicators: <FiNavigation size={28} />,
  'mobile-holders': <MdSmartphone size={28} />,
  mirrors: <GiMirrorMirror size={28} />,
  'fiber-items': <GiPackedPlanks size={28} />,
  headlights: <FaCarSide size={26} />,
  'spray-lube-items': <GiSpray size={28} />,
  perfumes: <GiPerfumeBottle size={28} />,
  exhaust: <GiStraightPipe size={28} />,
  visor: <GiVisoredHelm size={28} />,
  throttles: <GiSpeedometer size={28} />,
  'fog-lamp': <GiLightProjector size={28} />
};
const categoryIcon = (slug) => CATEGORY_ICONS[slug] || <FiGrid size={28} />;

const VALUE_PROPS = [
  { icon: <FiShield size={26} />, title: '100% Genuine Parts', text: 'Every product is sourced directly from authorized brands and distributors — no counterfeits, ever.' },
  { icon: <FiTruck size={26} />, title: 'Fast, Tracked Shipping', text: 'Orders are dispatched quickly and can be tracked right from your account or via Track Order.' },
  { icon: <FiTool size={26} />, title: 'Fitment You Can Trust', text: 'Every listing includes compatibility details so you order the right part the first time.' },
  { icon: <FiAward size={26} />, title: 'Warranty Backed', text: 'Applicable parts ship with manufacturer warranty and our own quality assurance.' }
];

const PROCESS_STEPS = [
  { step: '01', title: 'Sourcing', text: 'We partner directly with trusted manufacturers and authorized distributors for every category we stock.' },
  { step: '02', title: 'Quality Check', text: 'Each part is inspected for build quality, packaging integrity, and fitment accuracy before listing.' },
  { step: '03', title: 'Inventory & Cataloguing', text: 'Parts are catalogued with precise SKU, brand, and vehicle compatibility data.' },
  { step: '04', title: 'Order Processing', text: 'Orders are packed securely to prevent transit damage to sensitive components.' },
  { step: '05', title: 'Dispatch', text: 'Fast dispatch with tracking, so you always know where your order is.' },
  { step: '06', title: 'After-Sales Support', text: 'Our team is on hand for fitment questions, warranty claims, and order support.' }
];

const Home = () => {
  const [landing, setLanding] = useState(null);
  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/api/landing'),
      api.get('/api/products?featured=true&limit=8'),
      api.get('/api/branches'),
      api.get('/api/categories'),
      api.get('/api/products/category-counts')
    ]).then(([landingRes, productsRes, branchesRes, categoriesRes, countsRes]) => {
      setLanding(landingRes.data);
      setProducts(productsRes.data.products || []);
      setBranches(branchesRes.data || []);
      setCategories(categoriesRes.data || []);
      setCategoryCounts(countsRes.data || {});
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <LoadingSpinner fullPage />;

  const carouselImages = landing?.carouselImages || [];
  const categoriesWithProducts = categories.filter((c) => categoryCounts[c.slug] > 0);

  return (
    <div>
      {/* HERO */}
      <section style={{ position: 'relative', minHeight: '78vh', display: 'flex', alignItems: 'center', overflow: 'hidden', background: 'var(--black-surface)' }}>
        {carouselImages.length > 0 && (
          <Slider dots infinite autoplay autoplaySpeed={5000} speed={800} slidesToShow={1} slidesToScroll={1} arrows={false}
            style={{ position: 'absolute', inset: 0 }}>
            {carouselImages.map((img, i) => (
              <div key={i} style={{ position: 'relative', height: '78vh' }}>
                <img src={getImageUrl(img.url)} onError={handleImageError} alt={img.alt || 'One Clutch'} style={{ width: '100%', height: '78vh', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(11,24,48,0.05) 0%, rgba(11,24,48,0.08) 55%, rgba(11,24,48,0.38) 100%)' }} />
              </div>
            ))}
          </Slider>
        )}

        <div className="container fade-in-up" style={{ position: 'relative', textAlign: 'center', zIndex: 2 }}>
          <div className="section-label" style={{ color: '#ffffff', opacity: 0.95, textShadow: '0 1px 10px rgba(0,0,0,0.5)' }}>Genuine Auto Parts &amp; Accessories</div>
          <h1 style={{
            fontSize: 'clamp(2.4rem,7vw,4.5rem)', color: '#ffffff', marginBottom: '1rem',
            textShadow: '0 2px 20px rgba(0,0,0,0.55)'
          }}>
            {landing?.heroTitle || 'ONE CLUTCH'}
          </h1>
          <p style={{ fontSize: 'clamp(1rem,2vw,1.3rem)', color: '#ffffff', maxWidth: 600, margin: '0 auto 2rem', textShadow: '0 1px 10px rgba(0,0,0,0.5)' }}>
            {landing?.heroSubtitle || 'Genuine Parts. Perfect Fit. Every Time.'}
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
            <Link to="/shop?vehicleType=car" className="btn btn-lg" style={{ background: '#ffffff', color: 'var(--gold-dark)', fontWeight: 700, boxShadow: '0 4px 16px rgba(0,0,0,0.25)' }}>Shop Car Parts</Link>
            <Link to="/shop?vehicleType=bike" className="btn btn-outline btn-lg" style={{ borderColor: '#ffffff', color: '#ffffff', background: 'rgba(11,24,48,0.25)' }}>Shop Bike Parts</Link>
          </div>
          <div>
            <Link to="/shop" className="btn btn-primary btn-lg" style={{ minWidth: 220 }}>Shop All</Link>
          </div>
        </div>
      </section>

      {/* GARAGE SCENE STRIP */}
      <section style={{ background: 'var(--black-rich)', borderBottom: '1px solid var(--black-border)' }}>
        <GarageScene />
      </section>

      {/* SHOP BY CATEGORY */}
      {categoriesWithProducts.length > 0 && (
        <section className="section">
          <div className="container">
            <div className="section-label" style={{ textAlign: 'center' }}>Browse</div>
            <h2 className="section-title">Shop by Category</h2>
            <p className="section-subtitle">Jump straight to the parts you need</p>
            <div className="category-grid">
              {categoriesWithProducts.map((c) => (
                <Link
                  key={c._id}
                  to={`/shop?category=${encodeURIComponent(c.slug)}`}
                  className="card fade-in category-box"
                >
                  <div className="category-box-icon">
                    {categoryIcon(c.slug)}
                  </div>
                  <h3 className="category-box-name">{c.name}</h3>
                  <span className="category-box-count">
                    {categoryCounts[c.slug]} product{categoryCounts[c.slug] === 1 ? '' : 's'}
                  </span>
                  <span className="category-box-cta">
                    Shop Now <FiArrowRight size={13} />
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <style>{`
            .category-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 1.5rem;
            }
            .category-box {
              display: flex; flex-direction: column; align-items: center; text-align: center;
              padding: 2rem 1.25rem; text-decoration: none; gap: 0.85rem;
            }
            .category-box-icon {
              width: 64px; height: 64px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
              background: rgba(var(--accent-rgb),0.1); color: var(--gold);
            }
            .category-box-name { font-size: 1rem; text-transform: none; color: var(--text-primary); margin: 0; }
            .category-box-count { font-size: 0.78rem; color: var(--text-muted); }
            .category-box-cta {
              display: inline-flex; align-items: center; gap: 0.3rem; font-size: 0.78rem;
              font-weight: 600; color: var(--gold); text-transform: uppercase; letter-spacing: 0.05em;
            }

            @media (max-width: 1100px) {
              .category-grid { grid-template-columns: repeat(3, 1fr); }
            }
            @media (max-width: 640px) {
              .category-grid { grid-template-columns: repeat(3, 1fr); gap: 0.6rem; }
              .category-box { padding: 0.9rem 0.5rem; gap: 0.4rem; border-radius: var(--radius); }
              .category-box-icon { width: 38px; height: 38px; }
              .category-box-icon svg { width: 18px; height: 18px; }
              .category-box-name { font-size: 0.72rem; line-height: 1.2; }
              .category-box-count { font-size: 0.62rem; }
              .category-box-cta { font-size: 0.6rem; gap: 0.2rem; letter-spacing: 0.02em; }
              .category-box-cta svg { width: 10px; height: 10px; }
            }
          `}</style>
        </section>
      )}

      {/* FEATURED PRODUCTS */}
      {products.length > 0 && (
        <section className="section section-dark">
          <div className="container">
            <div className="section-label" style={{ textAlign: 'center' }}>Handpicked For You</div>
            <h2 className="section-title">Featured Parts</h2>
            <p className="section-subtitle">Popular picks across our car and bike accessory range</p>
            <div className="grid-4 featured-grid">
              {products.map((p) => <ProductCard key={p._id} product={p} />)}
            </div>
            <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
              <Link to="/shop" className="btn btn-outline">View All Products</Link>
            </div>
          </div>

          <style>{`
            @media (max-width: 480px) {
              .featured-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 0.85rem; }
            }
          `}</style>
        </section>
      )}

      {/* VALUE PROPS */}
      <section className="section">
        <div className="container">
          <div className="section-label" style={{ textAlign: 'center' }}>Why One Clutch</div>
          <h2 className="section-title">Built for Reliability</h2>
          <div className="gold-divider" />
          <div className="grid-4">
            {VALUE_PROPS.map((v) => (
              <div key={v.title} className="card" style={{ padding: '1.75rem', textAlign: 'center' }}>
                <div style={{ color: 'var(--gold)', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>{v.icon}</div>
                <h3 style={{ fontSize: '1rem', textTransform: 'none', color: 'var(--text-primary)', marginBottom: '0.6rem' }}>{v.title}</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section section-surface">
        <div className="container">
          <div className="section-label" style={{ textAlign: 'center' }}>Our Process</div>
          <h2 className="section-title">From Sourcing to Your Garage</h2>
          <div className="gold-divider" />
          <div className="grid-3">
            {PROCESS_STEPS.map((s) => (
              <div key={s.step} className="card" style={{ padding: '1.75rem' }}>
                <div style={{ fontFamily: 'var(--font-heading)', fontSize: '1.6rem', color: 'var(--gold)', marginBottom: '0.5rem' }}>{s.step}</div>
                <h3 style={{ fontSize: '1rem', textTransform: 'none', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>{s.title}</h3>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* YOUTUBE */}
      {landing?.youtubeVideoId && (
        <section className="section">
          <div className="container">
            <div className="section-label" style={{ textAlign: 'center' }}>Watch</div>
            <h2 className="section-title">{landing.youtubeTitle || 'Our Story'}</h2>
            <div className="gold-divider" />
            <div style={{ maxWidth: 900, margin: '0 auto', aspectRatio: '16/9', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              <YouTube videoId={landing.youtubeVideoId} opts={{ width: '100%', height: '100%' }} style={{ width: '100%', height: '100%' }} />
            </div>
          </div>
        </section>
      )}

      {/* BRANCHES */}
      {branches.length > 0 && (
        <section className="section section-dark">
          <div className="container">
            <div className="section-label" style={{ textAlign: 'center' }}>Visit Us</div>
            <h2 className="section-title">Our Branches</h2>
            <div className="gold-divider" />
            <div className="grid-3">
              {branches.map((b) => (
                <div key={b._id} className="card" style={{ padding: '1.5rem' }}>
                  <h3 style={{ fontSize: '1.05rem', textTransform: 'none', color: 'var(--gold)', marginBottom: '0.4rem' }}>{b.name}</h3>
                  <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>{b.address}</p>
                  {b.timings && <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{b.timings}</p>}
                  {b.phone && <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{b.phone}</p>}
                  {b.googleMapLink && (
                    <a href={b.googleMapLink} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ marginTop: '0.75rem' }}>
                      Get Directions
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section" style={{ background: 'linear-gradient(135deg, var(--gold-dark), var(--gold))', textAlign: 'center' }}>
        <div className="container">
          <h2 className="section-title" style={{ color: '#ffffff' }}>Find the Right Part, Right Now</h2>
          <p className="section-subtitle" style={{ color: '#eef3ff' }}>Browse our full catalog of car and bike parts and accessories.</p>
          <Link to="/shop" className="btn btn-lg" style={{ background: '#ffffff', color: 'var(--gold-dark)', fontWeight: 700 }}>Shop All Products</Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
